import { encolarAccion, esFaltaDeSenal } from '@/lib/campo/colaDeAcciones'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'

/**
 * Compuerta 2 · Precierre.
 *
 * La orden se cierra sola cuando se acaba su programación. Desde ahí el
 * colaborador pide más tiempo con justificación y el Gerente operativo decide.
 * Las dos reglas —justificación obligatoria y quién decide— viven en la base;
 * aquí solo se llama.
 */

/** Invalida todo lo que cambia cuando una orden de trabajo se mueve. */
function useRefrescar() {
  const clienteConsultas = useQueryClient()

  return async () => {
    await Promise.all([
      clienteConsultas.invalidateQueries({ queryKey: ['ordenes-servicio'] }),
      clienteConsultas.invalidateQueries({ queryKey: ['avisos'] }),
      clienteConsultas.invalidateQueries({ queryKey: ['cronograma'] }),
      clienteConsultas.invalidateQueries({ queryKey: ['recorridos'] }),
      clienteConsultas.invalidateQueries({ queryKey: ['cuello-de-botella'] }),
    ])
  }
}

export function useSolicitarMasTiempo() {
  const refrescar = useRefrescar()

  return useMutation({
    mutationFn: async ({
      ordenTrabajoId,
      horas,
      justificacion,
    }: {
      ordenTrabajoId: string
      horas: number
      justificacion: string
    }): Promise<void> => {
      // Se pide desde la obra, donde a menudo no hay señal. Se guarda con
      // la hora en que se pidió: quien decide necesita saber que la petición
      // es de las siete de la mañana y no de ahora.
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        await encolarAccion('mas-tiempo', ordenTrabajoId, {
          p_horas: horas,
          p_justificacion: justificacion,
        })
        return
      }

      const { error } = await supabase.rpc('fn_solicitar_mas_tiempo', {
        p_orden_trabajo_id: ordenTrabajoId,
        p_horas: horas,
        p_justificacion: justificacion,
      })

      // Señal débil: se guarda igual que sin red, en vez de un error.
      if (error && esFaltaDeSenal(error)) {
        await encolarAccion('mas-tiempo', ordenTrabajoId, {
          p_horas: horas,
          p_justificacion: justificacion,
        })
        return
      }
      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })
}

export function useDecidirMasTiempo() {
  const refrescar = useRefrescar()

  return useMutation({
    mutationFn: async ({
      ordenTrabajoId,
      aprobar,
      horasAprobadas,
      notas,
    }: {
      ordenTrabajoId: string
      aprobar: boolean
      horasAprobadas: number | null
      notas: string | null
    }): Promise<void> => {
      const { error } = await supabase.rpc('fn_decidir_mas_tiempo', {
        p_orden_trabajo_id: ordenTrabajoId,
        p_aprobar: aprobar,
        p_horas_aprobadas: horasAprobadas,
        p_notas: notas,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })
}

/**
 * Cerrar el trabajo, y dar por firmada el acta.
 *
 * LAS DOS PUERTAS QUE FALTABAN. En todo el proyecto no existía una sola forma
 * de escribir estado = 'cerrada' sobre una orden de trabajo —ni en las
 * migraciones ni en la aplicación— así que ningún trabajo podía terminar.
 * Tres órdenes de MEGA llevaban semanas paradas en precierre sin salida.
 *
 * Y no se notaba porque la tarea programada se llama «cerrar-órdenes-vencidas»
 * y lo que hace es ABRIR el precierre, no cerrarlo.
 */
export function useCerrarTrabajo() {
  const refrescar = useRefrescarPrecierre()

  return useMutation({
    mutationFn: async ({
      ordenTrabajoId,
      notas,
    }: {
      ordenTrabajoId: string
      notas: string | null
    }): Promise<void> => {
      const { error } = await supabase.rpc('fn_cerrar_orden_trabajo', {
        p_orden_trabajo_id: ordenTrabajoId,
        p_notas: notas,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })
}

export function useRegistrarFirma() {
  const refrescar = useRefrescarPrecierre()

  return useMutation({
    mutationFn: async ({
      ordenTrabajoId,
      notas,
    }: {
      ordenTrabajoId: string
      notas: string | null
    }): Promise<void> => {
      const { error } = await supabase.rpc('fn_registrar_firma_acta', {
        p_orden_trabajo_id: ordenTrabajoId,
        p_notas: notas,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })
}

/**
 * Cerrar mueve la orden de estación: se refresca todo lo que la muestra.
 * Las claves son las reales del proyecto —ver el comentario gemelo en
 * cotizaciones/useDecidirCotizacion.ts—; invalidar una que no existe no falla,
 * simplemente no refresca nada.
 */
function useRefrescarPrecierre() {
  const cliente = useQueryClient()

  return async () => {
    await Promise.all(
      [
        'ordenes-servicio', // la matriz y el detalle
        'pendientes-de-decision', // los contadores naranjas del menú
        'lo-que-espera', // el tablero
        'indicadores-cotizacion',
        'cuello-de-botella',
        'cronograma',
        'recorridos', // el tablero de recorrido (todas)
        'recorrido', // una orden en su recorrido
        'cotizaciones-wo',
        'avisos',
        'mi-trabajo', // la pantalla del técnico
      ].map((clave) => cliente.invalidateQueries({ queryKey: [clave] })),
    )
  }
}

/**
 * DEVOLVERLE EL TRABAJO AL TÉCNICO.
 *
 * La tercera salida del precierre, la que faltaba: ni cerrar —que le manda el
 * acta al cliente— ni dejar la orden quieta, sino regresarla a la calle
 * diciendo qué hay que corregir. El motivo es lo único que el técnico va a ver,
 * así que sin motivo no se manda.
 */
export function useDevolverAlTecnico() {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: async ({
      ordenTrabajoId,
      motivo,
    }: {
      ordenTrabajoId: string
      motivo: string
    }): Promise<void> => {
      const { error } = await supabase.rpc('fn_devolver_al_tecnico', {
        p_orden_trabajo_id: ordenTrabajoId,
        p_motivo: motivo,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['ordenes-servicio'] })
      await cliente.invalidateQueries({ queryKey: ['lo-que-espera'] })
    },
  })
}

/**
 * Gerencia pasa la orden a precierre cuando el técnico no puede (0269): sin
 * batería, sin la app o sin señal. Exige el motivo; queda en la bitácora.
 */
export function usePasarAPrecierre() {
  const refrescar = useRefrescar()

  return useMutation({
    mutationFn: async ({
      ordenTrabajoId,
      motivo,
    }: {
      ordenTrabajoId: string
      motivo: string
    }): Promise<void> => {
      const { error } = await supabase.rpc('fn_pasar_a_precierre', {
        p_orden_trabajo_id: ordenTrabajoId,
        p_motivo: motivo,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })
}
